import { getCurrentOperatorName } from '../../utils/currentOperator';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BaseTicketScreen from './BaseTicketScreen';
import { FormRow, FormGroup, Label, Input, Select, FieldError } from './shared/TicketStyledComponents';
import {
  SwapLegsContainer, SwapLegPanel, SwapLegHeader, SwapLegTitle, SwapLegIcon,
  SwapArrowDivider, SwapArrow, LegFormRow, LegFormRowSingle, SwapDirectionToggle, DirectionOption
} from './shared/SwapLegStyledComponents';
import { getTodayDate } from '../../utils/dateUtils';
import { calendarsFor } from '../../utils/businessDate';
import { provisionalSettleDate, resolveSettleDate, settlementCalendars, SettlementQuery } from '../../utils/settlement';
import { formatAmountWithCommas, parseAmountShorthand } from '../../utils/amountUtils';
import axios from 'axios';
import styled from 'styled-components';

import { DateField } from '../shared/DateField';

// ADR 0009 §4.2: a CDS (or CDX index trade) runs on its currency's calendar, not the book's. The
// ticket shows the settle date the server will store; the browser's count stands in until the
// server answers.
const cdsTicketType = (contractType?: string) => (contractType === 'INDEX' ? 'CDX' : 'CDS');

const settleQuery = (tradeDate: string, contractType?: string, currency?: string): SettlementQuery =>
  ({ tradeDate, ticketType: cdsTicketType(contractType), convention: 'T+1', currency: currency || 'USD' });

/** Applies the server's settle date if the form still holds the inputs and the date it was asked about. */
const applyServerSettleDate = (setFormData: any, query: SettlementQuery, expected: string) => {
  resolveSettleDate(query).then(settleDate => setFormData((prev: any) =>
    prev.tradeDate === query.tradeDate && (prev.currency || 'USD') === query.currency
      && prev.settleDate === expected && settleDate && settleDate !== expected
      ? { ...prev, settleDate } : prev));
};

/** After a trade date or currency change: the browser's count at once, then the server's. */
const refreshSettleDate = (setFormData: any, query: SettlementQuery) => {
  if (!query.tradeDate) return;
  const provisional = provisionalSettleDate(query);
  setFormData((prev: any) => ({ ...prev, settleDate: provisional }));
  applyServerSettleDate(setFormData, query, provisional);
};

/** A new ticket asks the server for its settle date on open; a reopened trade keeps its own. */
const SettleDateOnOpen: React.FC<{ formData: any; setFormData: any; editingTransactionId: number | null }> = ({
  formData, setFormData, editingTransactionId,
}) => {
  const location = useLocation();
  useEffect(() => {
    const state: any = location.state;
    if (editingTransactionId || state?.transactionId || state?.id || !formData.tradeDate) return;
    applyServerSettleDate(setFormData, settleQuery(formData.tradeDate, formData.contractType, formData.currency),
      formData.settleDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

// ── CDS-specific styled components ──

const ContractTypeToggle = styled.div`
  display: flex;
  gap: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(55, 65, 81, 0.5);
  width: fit-content;
`;

const ContractTypeOption = styled.button<{ active?: boolean }>`
  padding: 0.625rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.active
    ? 'rgba(59, 130, 246, 0.2)'
    : 'rgba(15, 23, 42, 0.7)'};
  color: ${props => props.active ? '#60a5fa' : '#6b7280'};
  border-right: 1px solid rgba(55, 65, 81, 0.3);

  &:last-child {
    border-right: none;
  }

  &:hover:not(:disabled) {
    background: rgba(59, 130, 246, 0.12);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContractTypeBadge = styled.span<{ active?: boolean }>`
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.active
    ? 'rgba(59, 130, 246, 0.3)'
    : 'rgba(75, 85, 99, 0.3)'};
  color: ${props => props.active ? '#93c5fd' : '#9ca3af'};
`;

const DerivativesCDSwapTicketScreen: React.FC = () => {
  const defaultTradeDate = getTodayDate(calendarsFor(cdsTicketType('SINGLE_NAME'), 'USD'));
  const initialFormData = {
    // Contract type
    contractType: 'SINGLE_NAME' as string,
    // Direction
    direction: 'BUY_PROTECTION' as string,
    assetManager: '',
    portfolioManager: '',
    assetClass: 'Derivatives',
    assetSubClass: 'Credit Default Swaps',
    security: '',
    // Common
    tradeDate: defaultTradeDate,
    settleDate: provisionalSettleDate(settleQuery(defaultTradeDate, 'SINGLE_NAME', 'USD')),
    effectiveDate: '',
    maturityDate: '',
    tenor: '5Y',
    currency: 'USD',
    notionalAmount: '',
    fixedRateBps: '100',  // Standard coupon: 100 or 500 bps
    marketSpreadBps: '',  // quoted par spread — drives RPV01 + ISDA upfront
    upfrontPct: '',       // optional dealer override (+ = buyer pays)
    portfolio: '',
    submittedBy: getCurrentOperatorName(),
    executedBy: '',
    notes: '',
    // Single-Name fields
    referenceEntity: '',
    referenceEntityTicker: '',
    redCode: '',
    creditEvents: 'Bankruptcy, Failure to Pay, Restructuring',
    settlementType: 'AUCTION',
    // Index fields
    indexName: '',
    indexSeries: '',
    indexVersion: '',
    // Clearing
    isCleared: 'true',
    clearinghouse: 'ICE Clear Credit',
    documentationType: 'ISDA_2014',
  };

  const validateForm = (formData: any, action: string): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};

    if (!formData.assetManager?.trim()) errors.assetManager = 'Asset Manager is required';
    if (!formData.portfolioManager?.trim()) errors.portfolioManager = 'Portfolio Manager is required';
    if (!formData.notionalAmount?.trim()) errors.notionalAmount = 'Notional Amount is required';
    if (!formData.fixedRateBps?.trim()) errors.fixedRateBps = 'Fixed Rate (Coupon) is required';
    if (!formData.marketSpreadBps?.trim()) errors.marketSpreadBps = 'Market Spread is required';
    if (!formData.tenor?.trim() && !formData.maturityDate) errors.tenor = 'Tenor or Maturity Date is required';
    if (!formData.portfolio?.trim()) errors.portfolio = 'Portfolio is required';
    if (!formData.tradeDate) errors.tradeDate = 'Trade Date is required';
    if (!formData.settleDate) errors.settleDate = 'Settle Date is required';

    if (formData.contractType === 'SINGLE_NAME') {
      if (!formData.referenceEntity?.trim()) errors.referenceEntity = 'Reference Entity is required';
    } else {
      if (!formData.indexName?.trim()) errors.indexName = 'Index Name is required';
      if (!formData.indexSeries?.trim()) errors.indexSeries = 'Series is required';
    }

    if (action === 'execute') {
      if (!formData.executedBy?.trim()) errors.executedBy = 'Executed By is required for execution';
    }

    return errors;
  };

  // CDS E2E rewire (2026-08-06, IRS template): the ticket's terms ARE the
  // bespoke contract — /prepare mints cds_contract + a classified per-contract
  // SMF (CD-prefix 9-char CUSIP) and returns the sourceRef the posting SPI
  // resolves at POST, plus the server-computed inception economics. The old
  // path booked a bare, contract-less trade against a hand-typed identifier
  // and never even sent the upfront it collected.
  const buildTransactionData = async (formData: any, dimensionData: any): Promise<any> => {
    const portfolio = dimensionData.portfolios.find((p: any) => p.portfolioName === formData.portfolio.trim());
    if (!portfolio) throw new Error(`Portfolio "${formData.portfolio}" not found`);

    const notional = parseAmountShorthand(formData.notionalAmount);
    const isIndex = formData.contractType === 'INDEX';
    const prepareRes = await axios.post('/api/derivatives/cds/prepare', {
      portfolioId: portfolio.id,
      contractType: formData.contractType,
      direction: formData.direction,
      notional,
      currency: formData.currency || 'USD',
      fixedRateBps: formData.fixedRateBps ? parseFloat(formData.fixedRateBps) : null,
      marketSpreadBps: formData.marketSpreadBps ? parseFloat(formData.marketSpreadBps) : null,
      upfrontPct: formData.upfrontPct?.trim() ? parseFloat(formData.upfrontPct) : null,
      referenceEntity: isIndex ? null : formData.referenceEntity,
      referenceEntityTicker: isIndex ? null : (formData.referenceEntityTicker || null),
      redCode: isIndex ? null : (formData.redCode || null),
      indexName: isIndex ? formData.indexName : null,
      indexSeries: isIndex && formData.indexSeries ? parseInt(formData.indexSeries, 10) : null,
      indexVersion: isIndex && formData.indexVersion ? parseInt(formData.indexVersion, 10) : null,
      tradeDate: formData.tradeDate,
      effectiveDate: formData.effectiveDate || formData.settleDate || null,
      maturityDate: formData.maturityDate || null,
      tenor: formData.tenor || null,
      settlementType: formData.settlementType || null,
      documentationType: formData.documentationType || null,
      isCleared: formData.isCleared === 'true',
      clearinghouse: formData.isCleared === 'true' ? formData.clearinghouse : null,
    });
    const { securityId, sourceRef, ticketType, inceptionCash } = prepareRes.data;

    return {
      transactionType: formData.direction === 'BUY_PROTECTION' ? 'BUY' : 'SELL',
      securityId,
      portfolioId: portfolio.id,
      // quantity = notional; price 100 is the par-style convention. amount =
      // notional (blotter $); netAmount = the server-computed SIGNED inception
      // cash (±upfront ± accrued rebate, positive = we receive) — the posting
      // SPI recomputes the same quantity from the contract and books it as
      // real cash (unlike IRS zero-cash, a CDS upfront is a real exchange).
      quantity: notional,
      price: 100,
      amount: notional,
      netAmount: inceptionCash ?? 0,
      tradeDate: formData.tradeDate,
      settleDate: formData.settleDate,
      notes: formData.notes || `${ticketType} ${formData.direction === 'BUY_PROTECTION' ? 'Buy' : 'Sell'} Protection ${formData.fixedRateBps}bps running @ ${formData.marketSpreadBps}bps ${formData.tenor || ''}`,
      createdBy: formData.submittedBy,
      executedBy: formData.executedBy || null,
      status: 'DRAFT',
      ticketType,
      eventType: 'ACQUIRE',
      sourceRef,
    };
  };

  // Reopen/edit: contract terms live on cds_contract (resolved by sourceRef),
  // not as flat trade fields.
  const loadTransactionData = async (transaction: any) => {
    const contractType = transaction.ticketType === 'CDX' ? 'INDEX' : 'SINGLE_NAME';
    const tradeDate = transaction.tradeDate
      || getTodayDate(calendarsFor(cdsTicketType(contractType), transaction.currency));
    const base = {
      contractType,
      direction: transaction.transactionType === 'SELL' ? 'SELL_PROTECTION' : 'BUY_PROTECTION',
      assetManager: transaction.assetManagerName || transaction.assetManager || '',
      portfolioManager: transaction.portfolioManagerName || transaction.portfolioManager || '',
      assetClass: 'Derivatives',
      assetSubClass: 'Credit Default Swaps',
      security: transaction.securityName || transaction.security || '',
      tradeDate,
      settleDate: transaction.settleDate
        || provisionalSettleDate(settleQuery(tradeDate, contractType, transaction.currency)),
      effectiveDate: '',
      maturityDate: '',
      tenor: '',
      currency: transaction.currency || 'USD',
      notionalAmount: transaction.amount != null ? transaction.amount.toString() : '',
      fixedRateBps: '100',
      marketSpreadBps: '',
      upfrontPct: '',
      portfolio: transaction.portfolioName || transaction.portfolio || '',
      submittedBy: transaction.createdBy || getCurrentOperatorName(),
      executedBy: transaction.executedBy || '',
      notes: transaction.notes || '',
      referenceEntity: '',
      referenceEntityTicker: '',
      redCode: '',
      creditEvents: 'Bankruptcy, Failure to Pay, Restructuring',
      settlementType: 'AUCTION',
      indexName: '',
      indexSeries: '',
      indexVersion: '',
      isCleared: 'true',
      clearinghouse: 'ICE Clear Credit',
      documentationType: 'ISDA_2014',
    };
    const m = (transaction.sourceRef || '').match(/cd[sx]-contract:(\d+)/);
    if (!m) return base;
    try {
      const c = (await axios.get(`/api/derivatives/cds/contract/${m[1]}`)).data;
      const isIndex = c.contractType === 'INDEX';
      return {
        ...base,
        contractType: isIndex ? 'INDEX' : 'SINGLE_NAME',
        direction: c.direction === 'SELL_PROTECTION' ? 'SELL_PROTECTION' : 'BUY_PROTECTION',
        effectiveDate: c.effectiveDate || '',
        maturityDate: c.maturityDate || '',
        currency: c.currency || base.currency,
        notionalAmount: c.notionalAmount != null ? String(c.notionalAmount) : base.notionalAmount,
        fixedRateBps: c.fixedRateBps != null ? String(parseFloat(c.fixedRateBps)) : base.fixedRateBps,
        marketSpreadBps: c.currentSpreadBps != null ? String(parseFloat(c.currentSpreadBps)) : '',
        upfrontPct: c.upfrontPct != null ? String(parseFloat(c.upfrontPct)) : '',
        referenceEntity: isIndex ? '' : (c.referenceEntityName || ''),
        referenceEntityTicker: c.referenceEntityTicker || '',
        redCode: c.redCode || '',
        settlementType: c.settlementType || base.settlementType,
        documentationType: c.documentationType || base.documentationType,
        isCleared: c.isCleared === false ? 'false' : 'true',
        clearinghouse: c.clearinghouse || base.clearinghouse,
        indexName: c.indexName || '',
        indexSeries: c.indexSeries != null ? String(c.indexSeries) : '',
        indexVersion: c.indexVersion != null ? String(c.indexVersion) : '',
      };
    } catch { return base; }
  };

  return (
    <BaseTicketScreen
      title="Credit Default Swap Trade Entry"
      assetClass="Derivatives"
      assetSubClass="Credit Default Swaps"
      hidePortfolioField
      initialFormData={initialFormData}
      validateForm={validateForm}
      buildTransactionData={buildTransactionData}
      loadTransactionData={loadTransactionData}
    >
      {({ formData, setFormData, errors, dimensionData, transactionStatus, isFieldDisabled, handleInputChange, editingTransactionId }) => {
        const isSingleName = formData.contractType === 'SINGLE_NAME';
        const isBuyProtection = formData.direction === 'BUY_PROTECTION';

        return (
          <>
            <SettleDateOnOpen formData={formData} setFormData={setFormData} editingTransactionId={editingTransactionId} />

            {/* ── Row 1: Contract Type, Direction ── */}
            <FormRow>
              <FormGroup>
                <Label>Contract Type*</Label>
                <ContractTypeToggle>
                  <ContractTypeOption
                    active={isSingleName}
                    disabled={isFieldDisabled('contractType')}
                    onClick={() => handleInputChange('contractType', 'SINGLE_NAME')}
                  >
                    Single-Name
                    <ContractTypeBadge active={isSingleName}>CDS</ContractTypeBadge>
                  </ContractTypeOption>
                  <ContractTypeOption
                    active={!isSingleName}
                    disabled={isFieldDisabled('contractType')}
                    onClick={() => handleInputChange('contractType', 'INDEX')}
                  >
                    Index
                    <ContractTypeBadge active={!isSingleName}>CDX</ContractTypeBadge>
                  </ContractTypeOption>
                </ContractTypeToggle>
              </FormGroup>

              <FormGroup>
                <Label>Direction*</Label>
                <SwapDirectionToggle>
                  <DirectionOption
                    variant="receive"
                    active={isBuyProtection}
                    disabled={isFieldDisabled('direction')}
                    onClick={() => handleInputChange('direction', 'BUY_PROTECTION')}
                  >
                    Buy Protection
                  </DirectionOption>
                  <DirectionOption
                    variant="pay"
                    active={!isBuyProtection}
                    disabled={isFieldDisabled('direction')}
                    onClick={() => handleInputChange('direction', 'SELL_PROTECTION')}
                  >
                    Sell Protection
                  </DirectionOption>
                </SwapDirectionToggle>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="tradeDate">Trade Date*</Label>
                <DateField
                  id="tradeDate"
                  value={formData.tradeDate ?? ''}
                  className={errors.tradeDate ? 'error' : ''}
                  disabled={isFieldDisabled('tradeDate')}
                  calendars={calendarsFor(cdsTicketType(formData.contractType), formData.currency)}
                  onChange={iso => {
                    handleInputChange('tradeDate', iso);
                    refreshSettleDate(setFormData, settleQuery(iso, formData.contractType, formData.currency));
                  }}
                />
                {errors.tradeDate && <FieldError>{errors.tradeDate}</FieldError>}
              </FormGroup>
            </FormRow>

            {/* ── Row 2: Asset Manager, PM, Executed By ── */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="assetManager">Asset Manager*</Label>
                <Select
                  id="assetManager"
                  value={formData.assetManager}
                  onChange={(e) => handleInputChange('assetManager', e.target.value)}
                  className={errors.assetManager ? 'error' : ''}
                  disabled={isFieldDisabled('assetManager')}
                >
                  <option value="">Select Asset Manager</option>
                  {dimensionData.assetManagers.map((am: any) => (
                    <option key={am.id} value={am.assetManagerName}>{am.assetManagerName}</option>
                  ))}
                </Select>
                {errors.assetManager && <FieldError>{errors.assetManager}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="portfolioManager">Portfolio Manager*</Label>
                <Select
                  id="portfolioManager"
                  value={formData.portfolioManager}
                  onChange={(e) => handleInputChange('portfolioManager', e.target.value)}
                  className={errors.portfolioManager ? 'error' : ''}
                  disabled={isFieldDisabled('portfolioManager')}
                >
                  <option value="">Select Portfolio Manager</option>
                  {dimensionData.portfolioManagers.map((pm: any) => (
                    <option key={pm.id} value={pm.portfolioManagerName}>{pm.portfolioManagerName}</option>
                  ))}
                </Select>
                {errors.portfolioManager && <FieldError>{errors.portfolioManager}</FieldError>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="executedBy">Executed By{transactionStatus === 'SENT' ? '*' : ''}</Label>
                <Select
                  id="executedBy"
                  value={formData.executedBy}
                  onChange={(e) => handleInputChange('executedBy', e.target.value)}
                  disabled={isFieldDisabled('executedBy')}
                  className={errors.executedBy ? 'error' : ''}
                >
                  <option value="">Select</option>
                  {dimensionData.users.map((user: any) => (
                    <option key={user.id} value={user.fullName}>{user.fullName} {user.role ? `(${user.role})` : ''}</option>
                  ))}
                </Select>
                {errors.executedBy && <FieldError>{errors.executedBy}</FieldError>}
              </FormGroup>
            </FormRow>

            {/* ── Row 3: Reference Entity / Index fields (conditional) ── */}
            {isSingleName ? (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="referenceEntity">Reference Entity*</Label>
                  <Input
                    id="referenceEntity"
                    type="text"
                    value={formData.referenceEntity}
                    onChange={(e) => handleInputChange('referenceEntity', e.target.value)}
                    placeholder="e.g., Ford Motor Company"
                    className={errors.referenceEntity ? 'error' : ''}
                    disabled={isFieldDisabled('referenceEntity')}
                  />
                  {errors.referenceEntity && <FieldError>{errors.referenceEntity}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="referenceEntityTicker">Ticker</Label>
                  <Input
                    id="referenceEntityTicker"
                    type="text"
                    value={formData.referenceEntityTicker}
                    onChange={(e) => handleInputChange('referenceEntityTicker', e.target.value)}
                    placeholder="e.g., F"
                    disabled={isFieldDisabled('referenceEntityTicker')}
                  />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="redCode">RED Code</Label>
                  <Input
                    id="redCode"
                    type="text"
                    value={formData.redCode}
                    onChange={(e) => handleInputChange('redCode', e.target.value)}
                    placeholder="Markit RED code"
                    disabled={isFieldDisabled('redCode')}
                  />
                </FormGroup>
              </FormRow>
            ) : (
              <FormRow>
                <FormGroup>
                  <Label htmlFor="indexName">Index Name*</Label>
                  <Select
                    id="indexName"
                    value={formData.indexName}
                    onChange={(e) => handleInputChange('indexName', e.target.value)}
                    className={errors.indexName ? 'error' : ''}
                    disabled={isFieldDisabled('indexName')}
                  >
                    <option value="">Select Index</option>
                    <option value="CDX.NA.IG">CDX.NA.IG (North America Investment Grade)</option>
                    <option value="CDX.NA.HY">CDX.NA.HY (North America High Yield)</option>
                    <option value="CDX.EM">CDX.EM (Emerging Markets)</option>
                    <option value="iTraxx Europe">iTraxx Europe (Investment Grade)</option>
                    <option value="iTraxx Crossover">iTraxx Crossover (High Yield)</option>
                    <option value="iTraxx Asia">iTraxx Asia ex-Japan</option>
                  </Select>
                  {errors.indexName && <FieldError>{errors.indexName}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="indexSeries">Series*</Label>
                  <Input
                    id="indexSeries"
                    type="number"
                    value={formData.indexSeries}
                    onChange={(e) => handleInputChange('indexSeries', e.target.value)}
                    placeholder="e.g., 42"
                    className={errors.indexSeries ? 'error' : ''}
                    disabled={isFieldDisabled('indexSeries')}
                  />
                  {errors.indexSeries && <FieldError>{errors.indexSeries}</FieldError>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="indexVersion">Version</Label>
                  <Input
                    id="indexVersion"
                    type="number"
                    value={formData.indexVersion}
                    onChange={(e) => handleInputChange('indexVersion', e.target.value)}
                    placeholder="e.g., 1"
                    disabled={isFieldDisabled('indexVersion')}
                  />
                </FormGroup>
              </FormRow>
            )}

            {/* ── Row 4: Contract Name, Settle Date ── */}
            {/* Identifier field REMOVED (CDS E2E 2026-08-06): the contract +
                classified SMF mint from the ticket's terms at /prepare —
                there is nothing to type. */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="security">Contract Name</Label>
                <Input
                  id="security"
                  type="text"
                  value={formData.security}
                  onChange={(e) => handleInputChange('security', e.target.value)}
                  placeholder={isSingleName ? 'e.g., Ford 5Y CDS' : 'e.g., CDX.NA.IG S42 5Y'}
                  disabled={isFieldDisabled('security')}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="settleDate">Settle Date*</Label>
                <DateField
                  id="settleDate"
                  value={formData.settleDate ?? ''}
                  className={errors.settleDate ? 'error' : ''}
                  disabled={isFieldDisabled('settleDate')}
                  calendars={settlementCalendars({ ticketType: cdsTicketType(formData.contractType), currency: formData.currency })}
                  onChange={iso => handleInputChange('settleDate', iso)}
                />
                {errors.settleDate && <FieldError>{errors.settleDate}</FieldError>}
              </FormGroup>
            </FormRow>

            {/* ════════════════════════════════════════════════════════════════ */}
            {/*  TWO-PANEL SWAP LEGS                                           */}
            {/* ════════════════════════════════════════════════════════════════ */}

            <SwapLegsContainer>
              {/* ── PROTECTION LEG (Premium Payer) ── */}
              <SwapLegPanel variant={isBuyProtection ? 'pay' : 'receive'}>
                <SwapLegHeader variant={isBuyProtection ? 'pay' : 'receive'}>
                  <SwapLegIcon variant={isBuyProtection ? 'pay' : 'receive'}>
                    {isBuyProtection ? '↑' : '↓'}
                  </SwapLegIcon>
                  <SwapLegTitle variant={isBuyProtection ? 'pay' : 'receive'}>
                    Protection Leg (Premium)
                  </SwapLegTitle>
                </SwapLegHeader>

                <LegFormRow>
                  <FormGroup>
                    <Label htmlFor="currency">Currency*</Label>
                    <Select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => {
                        handleInputChange('currency', e.target.value);
                        refreshSettleDate(setFormData, settleQuery(formData.tradeDate, formData.contractType, e.target.value));
                      }}
                      disabled={isFieldDisabled('currency')}
                    >
                      {dimensionData.currencies.map((c: any) => (
                        <option key={c.id} value={c.currencyCode}>{c.currencyCode}</option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="notionalAmount">Notional Amount*</Label>
                    <Input
                      id="notionalAmount"
                      type="text"
                      value={formData.notionalAmount}
                      onChange={(e) => handleInputChange('notionalAmount', e.target.value)}
                      onBlur={(e) => {
                        const parsed = parseAmountShorthand(e.target.value);
                        if (parsed > 0) {
                          setFormData((prev: any) => ({ ...prev, notionalAmount: formatAmountWithCommas(parsed) }));
                        }
                      }}
                      placeholder="e.g., 10MM"
                      className={errors.notionalAmount ? 'error' : ''}
                      disabled={isFieldDisabled('notionalAmount')}
                    />
                    {errors.notionalAmount && <FieldError>{errors.notionalAmount}</FieldError>}
                  </FormGroup>
                </LegFormRow>

                <LegFormRow>
                  <FormGroup>
                    <Label htmlFor="fixedRateBps">Fixed Rate / Coupon (bps)*</Label>
                    <Select
                      id="fixedRateBps"
                      value={formData.fixedRateBps}
                      onChange={(e) => handleInputChange('fixedRateBps', e.target.value)}
                      className={errors.fixedRateBps ? 'error' : ''}
                      disabled={isFieldDisabled('fixedRateBps')}
                    >
                      <option value="25">25 bps</option>
                      <option value="100">100 bps (Standard IG)</option>
                      <option value="500">500 bps (Standard HY)</option>
                    </Select>
                    {errors.fixedRateBps && <FieldError>{errors.fixedRateBps}</FieldError>}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="marketSpreadBps">Market Spread (bps)*</Label>
                    <Input
                      id="marketSpreadBps"
                      type="number"
                      step="1"
                      value={formData.marketSpreadBps}
                      onChange={(e) => handleInputChange('marketSpreadBps', e.target.value)}
                      placeholder="e.g., 275"
                      className={errors.marketSpreadBps ? 'error' : ''}
                      disabled={isFieldDisabled('marketSpreadBps')}
                    />
                    {errors.marketSpreadBps && <FieldError>{errors.marketSpreadBps}</FieldError>}
                  </FormGroup>
                </LegFormRow>

                <LegFormRow>
                  <FormGroup>
                    <Label htmlFor="upfrontPct">Upfront Points (%)</Label>
                    <Input
                      id="upfrontPct"
                      type="number"
                      step="0.01"
                      value={formData.upfrontPct}
                      onChange={(e) => handleInputChange('upfrontPct', e.target.value)}
                      placeholder="blank = derive from spread (+ = buyer pays)"
                      disabled={isFieldDisabled('upfrontPct')}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="tenor">Tenor*</Label>
                    <Select
                      id="tenor"
                      value={formData.tenor}
                      onChange={(e) => handleInputChange('tenor', e.target.value)}
                      className={errors.tenor ? 'error' : ''}
                      disabled={isFieldDisabled('tenor')}
                    >
                      <option value="">Select</option>
                      <option value="1Y">1Y</option>
                      <option value="3Y">3Y</option>
                      <option value="5Y">5Y (Standard)</option>
                      <option value="7Y">7Y</option>
                      <option value="10Y">10Y</option>
                    </Select>
                    {errors.tenor && <FieldError>{errors.tenor}</FieldError>}
                  </FormGroup>
                </LegFormRow>

                <LegFormRow>
                  <FormGroup>
                    <Label htmlFor="effectiveDate">Effective Date</Label>
                    <DateField
                      id="effectiveDate"
                      value={formData.effectiveDate ?? ''}
                      disabled={isFieldDisabled('effectiveDate')}
                      onChange={iso => handleInputChange('effectiveDate', iso)}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="maturityDate">Maturity Date</Label>
                    <DateField
                      id="maturityDate"
                      value={formData.maturityDate ?? ''}
                      disabled={isFieldDisabled('maturityDate')}
                      onChange={iso => handleInputChange('maturityDate', iso)}
                    />
                  </FormGroup>
                </LegFormRow>
              </SwapLegPanel>

              {/* ── ARROW DIVIDER ── */}
              <SwapArrowDivider>
                <SwapArrow>⇄</SwapArrow>
              </SwapArrowDivider>

              {/* ── CONTINGENT LEG (Credit Event Payout) ── */}
              <SwapLegPanel variant={isBuyProtection ? 'receive' : 'pay'}>
                <SwapLegHeader variant={isBuyProtection ? 'receive' : 'pay'}>
                  <SwapLegIcon variant={isBuyProtection ? 'receive' : 'pay'}>
                    {isBuyProtection ? '↓' : '↑'}
                  </SwapLegIcon>
                  <SwapLegTitle variant={isBuyProtection ? 'receive' : 'pay'}>
                    Contingent Leg (Credit Event)
                  </SwapLegTitle>
                </SwapLegHeader>

                {isSingleName ? (
                  <>
                    <LegFormRowSingle>
                      <FormGroup>
                        <Label htmlFor="creditEvents">Credit Events</Label>
                        <Input
                          id="creditEvents"
                          type="text"
                          value={formData.creditEvents}
                          onChange={(e) => handleInputChange('creditEvents', e.target.value)}
                          placeholder="Bankruptcy, Failure to Pay, Restructuring"
                          disabled={isFieldDisabled('creditEvents')}
                        />
                      </FormGroup>
                    </LegFormRowSingle>

                    <LegFormRow>
                      <FormGroup>
                        <Label htmlFor="settlementType">Settlement Type</Label>
                        <Select
                          id="settlementType"
                          value={formData.settlementType}
                          onChange={(e) => handleInputChange('settlementType', e.target.value)}
                          disabled={isFieldDisabled('settlementType')}
                        >
                          <option value="AUCTION">Auction</option>
                          <option value="PHYSICAL">Physical</option>
                          <option value="CASH">Cash</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="documentationType">Documentation</Label>
                        <Select
                          id="documentationType"
                          value={formData.documentationType}
                          onChange={(e) => handleInputChange('documentationType', e.target.value)}
                          disabled={isFieldDisabled('documentationType')}
                        >
                          <option value="ISDA_2014">ISDA 2014</option>
                          <option value="ISDA_2003">ISDA 2003</option>
                        </Select>
                      </FormGroup>
                    </LegFormRow>
                  </>
                ) : (
                  <>
                    <LegFormRowSingle>
                      <FormGroup>
                        <Label htmlFor="creditEvents">Credit Events</Label>
                        <Input
                          id="creditEvents"
                          type="text"
                          value={formData.creditEvents}
                          onChange={(e) => handleInputChange('creditEvents', e.target.value)}
                          placeholder="Bankruptcy, Failure to Pay"
                          disabled={isFieldDisabled('creditEvents')}
                        />
                      </FormGroup>
                    </LegFormRowSingle>

                    <LegFormRow>
                      <FormGroup>
                        <Label htmlFor="settlementType">Settlement Type</Label>
                        <Select
                          id="settlementType"
                          value={formData.settlementType}
                          onChange={(e) => handleInputChange('settlementType', e.target.value)}
                          disabled={isFieldDisabled('settlementType')}
                        >
                          <option value="AUCTION">Auction</option>
                          <option value="CASH">Cash</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <Label htmlFor="documentationType">Documentation</Label>
                        <Select
                          id="documentationType"
                          value={formData.documentationType}
                          onChange={(e) => handleInputChange('documentationType', e.target.value)}
                          disabled={isFieldDisabled('documentationType')}
                        >
                          <option value="ISDA_2014">ISDA 2014</option>
                          <option value="ISDA_2003">ISDA 2003</option>
                        </Select>
                      </FormGroup>
                    </LegFormRow>
                  </>
                )}

                <LegFormRow>
                  <FormGroup>
                    <Label htmlFor="isCleared">Cleared</Label>
                    <Select
                      id="isCleared"
                      value={formData.isCleared}
                      onChange={(e) => handleInputChange('isCleared', e.target.value)}
                      disabled={isFieldDisabled('isCleared')}
                    >
                      <option value="true">Yes</option>
                      <option value="false">No (Bilateral)</option>
                    </Select>
                  </FormGroup>

                  {formData.isCleared === 'true' && (
                    <FormGroup>
                      <Label htmlFor="clearinghouse">Clearinghouse</Label>
                      <Select
                        id="clearinghouse"
                        value={formData.clearinghouse}
                        onChange={(e) => handleInputChange('clearinghouse', e.target.value)}
                        disabled={isFieldDisabled('clearinghouse')}
                      >
                        <option value="ICE Clear Credit">ICE Clear Credit</option>
                        <option value="ICE Clear Europe">ICE Clear Europe</option>
                        <option value="LCH CDSClear">LCH CDSClear</option>
                      </Select>
                    </FormGroup>
                  )}
                </LegFormRow>
              </SwapLegPanel>
            </SwapLegsContainer>

            {/* ── Portfolio ── */}
            <FormRow>
              <FormGroup>
                <Label htmlFor="portfolio">Portfolio*</Label>
                <Select
                  id="portfolio"
                  value={formData.portfolio}
                  onChange={(e) => handleInputChange('portfolio', e.target.value)}
                  className={errors.portfolio ? 'error' : ''}
                  disabled={isFieldDisabled('portfolio')}
                >
                  <option value="">Select Portfolio</option>
                  {dimensionData.portfolios.map((p: any) => (
                    <option key={p.id} value={p.portfolioName}>{p.portfolioName}</option>
                  ))}
                </Select>
                {errors.portfolio && <FieldError>{errors.portfolio}</FieldError>}
              </FormGroup>
            </FormRow>
          </>
        );
      }}
    </BaseTicketScreen>
  );
};

export default DerivativesCDSwapTicketScreen;
